import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import './SalidaInventario.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import { getApiUrl } from '../../utils/api';
import { FaPlus, FaTrash, FaBox, FaCalendarAlt, FaClipboardList, FaEye, FaTruck } from 'react-icons/fa';

const SalidaInventario = () => {
  const [salidas, setSalidas] = useState([]);
  const [salidasAgrupadas, setSalidasAgrupadas] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [opcionesIngredientes, setOpcionesIngredientes] = useState([]);
  const [ingredienteSeleccionado, setIngredienteSeleccionado] = useState(null);
  const [platillos, setPlatillos] = useState([]);
  const [opcionesPlatillos, setOpcionesPlatillos] = useState([]);
  const [platilloSeleccionado, setPlatilloSeleccionado] = useState(null);
  const [platillosAgregados, setPlatillosAgregados] = useState([]);
  const [ingredientesConsolidados, setIngredientesConsolidados] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [opcionesDestinos, setOpcionesDestinos] = useState([]);
  const [destinoSeleccionado, setDestinoSeleccionado] = useState(null);
  const [destinoFiltro, setDestinoFiltro] = useState(null);
  const [modoSeleccion, setModoSeleccion] = useState('ingrediente');
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [salidaAEliminar, setSalidaAEliminar] = useState(null);
  const [salidaDetalle, setSalidaDetalle] = useState(null);

  // Campos del formulario
  const [formData, setFormData] = useState({
    idIngrediente: '',
    cantidad: '',
    motivo: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);

  const toast = useToast();

  // Función para limitar a 3 decimales
  const limitarDecimales = (valor, decimales = 3) => {
    const num = parseFloat(valor);
    if (isNaN(num)) return 0;
    return Math.round(num * Math.pow(10, decimales)) / Math.pow(10, decimales);
  };

  useEffect(() => {
    cargarDestinos();
    cargarIngredientes();
    cargarPlatillos();
  }, []);

  useEffect(() => {
    if (destinos.length > 0) {
      cargarSalidas();
    }
  }, [fechaInicio, fechaFin, destinoFiltro, destinos]);

  const cargarDestinos = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getDestinosSalidaEntrada' })
      });

      const data = await response.json();

      if (data.success) {
        // Filtrar para excluir Mermas (id=1) ya que tiene su propio módulo
        const destinosFiltrados = (data.data || []).filter(d => d.idDestinoSalidaEntrada !== 1);
        setDestinos(destinosFiltrados);

        const opciones = destinosFiltrados.map(destino => ({
          value: destino.idDestinoSalidaEntrada,
          label: destino.nombre
        }));
        setOpcionesDestinos(opciones);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar destinos:', error);
      toast.error('Error al cargar destinos');
    }
  };

  const cargarIngredientes = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getIngredientes' })
      });

      const data = await response.json();

      if (data.success) {
        setIngredientes(data.data || []);
        const opciones = (data.data || []).map(ingrediente => ({
          value: ingrediente.idIngrediente,
          label: ingrediente.nombre
        }));
        setOpcionesIngredientes(opciones);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar ingredientes:', error);
      toast.error('Error al cargar ingredientes');
    }
  };

  const cargarPlatillos = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getTodosLosPlatillos' })
      });

      const data = await response.json();

      if (data.success) {
        setPlatillos(data.data || []);
        const opciones = (data.data || []).map(platillo => ({
          value: platillo.idProducto,
          label: platillo.nombre
        }));
        setOpcionesPlatillos(opciones);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar platillos:', error);
      toast.error('Error al cargar platillos');
    }
  };

  const agregarPlatillo = async () => {
    if (!platilloSeleccionado) {
      toast.error('Seleccione un platillo');
      return;
    }

    if (platillosAgregados.find(p => p.idProducto === platilloSeleccionado.value)) {
      toast.error('Este platillo ya está en la lista');
      return;
    }

    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'getIngredientesProductoCompleto',
          idProducto: platilloSeleccionado.value
        })
      });

      const data = await response.json();

      if (data.success) {
        const nuevoPlatillo = {
          idProducto: platilloSeleccionado.value,
          nombre: platilloSeleccionado.label,
          cantidad: 1,
          ingredientes: data.data || []
        };

        const nuevosAgregados = [...platillosAgregados, nuevoPlatillo];
        setPlatillosAgregados(nuevosAgregados);
        setPlatilloSeleccionado(null);
        consolidarIngredientes(nuevosAgregados);
      } else {
        toast.error('Error al obtener ingredientes del platillo');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al agregar platillo');
    }
  };

  const eliminarPlatillo = (idProducto) => {
    const nuevosAgregados = platillosAgregados.filter(p => p.idProducto !== idProducto);
    setPlatillosAgregados(nuevosAgregados);
    consolidarIngredientes(nuevosAgregados);
  };

  const cambiarCantidadPlatillo = (idProducto, nuevaCantidad) => {
    const cantidad = parseFloat(nuevaCantidad) || 0;
    const nuevosAgregados = platillosAgregados.map(p =>
      p.idProducto === idProducto ? { ...p, cantidad } : p
    );
    setPlatillosAgregados(nuevosAgregados);
    consolidarIngredientes(nuevosAgregados);
  };

  const consolidarIngredientes = (listaPlatillos) => {
    const consolidado = {};

    listaPlatillos.forEach(platillo => {
      platillo.ingredientes.forEach(ing => {
        const cantidadTotal = parseFloat(ing.cantidad) * platillo.cantidad;

        if (consolidado[ing.idIngrediente]) {
          consolidado[ing.idIngrediente].cantidadSalida += cantidadTotal;
          if (ing.origen === 'preparado' && ing.preparado) {
            if (!consolidado[ing.idIngrediente].preparados.includes(ing.preparado)) {
              consolidado[ing.idIngrediente].preparados.push(ing.preparado);
            }
          }
        } else {
          consolidado[ing.idIngrediente] = {
            idIngrediente: ing.idIngrediente,
            nombre: ing.nombre,
            cantidadSalida: cantidadTotal,
            seleccionado: true,
            origen: ing.origen || 'directo',
            preparados: ing.preparado ? [ing.preparado] : []
          };
        }
      });
    });

    const resultado = Object.values(consolidado).map(ing => ({
      ...ing,
      cantidadSalida: limitarDecimales(ing.cantidadSalida, 3)
    }));

    setIngredientesConsolidados(resultado);
  };

  const handleIngredienteConsolidadoChange = (idIngrediente, field, value) => {
    setIngredientesConsolidados(prev => prev.map(ing => {
      if (ing.idIngrediente === idIngrediente) {
        return { ...ing, [field]: value };
      }
      return ing;
    }));
  };

  const cargarSalidas = async () => {
    try {
      setLoading(true);

      const payload = {
        tipo: 'getSalidasInventario',
        fechaInicio: fechaInicio,
        fechaFin: fechaFin
      };

      if (destinoFiltro) {
        payload.idDestino = destinoFiltro.value;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        // Filtrar para excluir mermas (destino 1)
        const salidasFiltradas = (data.data || []).filter(s => parseInt(s.idDestinoSalidaEntrada) !== 1);
        setSalidas(salidasFiltradas);

        // Agrupar por idSalida
        const agrupadas = {};
        salidasFiltradas.forEach(s => {
          if (!agrupadas[s.idSalida]) {
            agrupadas[s.idSalida] = {
              idSalida: s.idSalida,
              fecha: s.fecha,
              motivo: s.motivo,
              idDestinoSalidaEntrada: s.idDestinoSalidaEntrada,
              nombreDestino: s.nombreDestino,
              ingredientes: []
            };
          }
          agrupadas[s.idSalida].ingredientes.push({
            idSalidaDetalle: s.idSalidaDetalle,
            idIngrediente: s.idIngrediente,
            nombreIngrediente: s.nombreIngrediente,
            cantidad: s.cantidad,
            costo: s.costo
          });
        });

        setSalidasAgrupadas(Object.values(agrupadas));
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar salidas:', error);
      toast.error('Error al cargar salidas');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nuevaSalida = () => {
    setFormData({
      idIngrediente: '',
      cantidad: '',
      motivo: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setIngredienteSeleccionado(null);
    setPlatilloSeleccionado(null);
    setPlatillosAgregados([]);
    setIngredientesConsolidados([]);
    setDestinoSeleccionado(null);
    setModoSeleccion('ingrediente');
    setShowModal(true);
  };

  const guardarSalida = async () => {
    try {
      setGuardando(true);

      if (!destinoSeleccionado) {
        toast.error('Debe seleccionar un destino');
        return;
      }

      if (modoSeleccion === 'ingrediente') {
        if (!ingredienteSeleccionado) {
          toast.error('Debe seleccionar un producto');
          return;
        }

        if (!formData.cantidad || parseFloat(formData.cantidad) <= 0) {
          toast.error('La cantidad debe ser mayor a 0');
          return;
        }

        const payload = {
          tipo: 'crearSalidaInventario',
          idIngrediente: ingredienteSeleccionado.value,
          cantidad: formData.cantidad,
          motivo: formData.motivo || 'Salida de inventario',
          fecha: formData.fecha,
          idDestino: destinoSeleccionado.value
        };

        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
          toast.success('Salida registrada correctamente');
          handleCloseModal();
          cargarSalidas();
        } else {
          toast.error(data.message);
        }
      } else {
        // Modo platillo
        if (platillosAgregados.length === 0) {
          toast.error('Debe agregar al menos un platillo');
          return;
        }

        const ingredientesSeleccionados = ingredientesConsolidados.filter(ing => ing.seleccionado && ing.cantidadSalida > 0);

        if (ingredientesSeleccionados.length === 0) {
          toast.error('Debe seleccionar al menos un ingrediente con cantidad mayor a 0');
          return;
        }

        const ingredientesPayload = ingredientesSeleccionados.map(ing => ({
          idIngrediente: ing.idIngrediente,
          cantidad: ing.cantidadSalida
        }));

        const descripcionPlatillos = platillosAgregados
          .map(p => `${p.nombre} x${p.cantidad}`)
          .join(', ');

        const payload = {
          tipo: 'crearSalidaInventarioMultiple',
          ingredientes: ingredientesPayload,
          motivo: formData.motivo ? `${formData.motivo} (${descripcionPlatillos})` : descripcionPlatillos,
          fecha: formData.fecha,
          idDestino: destinoSeleccionado.value
        };

        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`${data.data.ingredientesRegistrados} ingredientes registrados como salida`);
          handleCloseModal();
          cargarSalidas();
        } else {
          toast.error(data.message);
        }
      }
    } catch (error) {
      console.error('Error al guardar salida:', error);
      toast.error('Error al guardar salida');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarSalida = (salida) => {
    setSalidaAEliminar(salida);
    setShowDeleteModal(true);
  };

  const eliminarSalida = async () => {
    if (!salidaAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarSalidaInventario',
          idSalida: salidaAEliminar.idSalida
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Salida eliminada correctamente');
        setShowDeleteModal(false);
        setSalidaAEliminar(null);
        cargarSalidas();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al eliminar salida:', error);
      toast.error('Error al eliminar salida');
    } finally {
      setGuardando(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIngredienteSeleccionado(null);
    setPlatilloSeleccionado(null);
    setPlatillosAgregados([]);
    setIngredientesConsolidados([]);
    setDestinoSeleccionado(null);
    setModoSeleccion('ingrediente');
    setFormData({
      idIngrediente: '',
      cantidad: '',
      motivo: '',
      fecha: new Date().toISOString().split('T')[0]
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSalidaAEliminar(null);
  };

  const verDetalleSalida = (salida) => {
    setSalidaDetalle(salida);
    setShowDetalleModal(true);
  };

  const handleCloseDetalleModal = () => {
    setShowDetalleModal(false);
    setSalidaDetalle(null);
  };

  if (loading && salidas.length === 0 && destinos.length === 0) {
    return (
      <div className="salida-inventario-container">
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="salida-inventario-container">
      <Header titulo='Salida de Inventario' color='#2e7d32' />
      <div className="salida-inventario-content">
        <div className="salida-inventario-inner">
          <div className="salida-inventario-header">
            <h1 className="salida-inventario-titulo">Salidas de Inventario</h1>
            <div className="botones-header">
              <button onClick={nuevaSalida} className="btn-nuevo">
                <FaPlus /> Nueva Salida
              </button>
            </div>
          </div>

          <div className="panel-filtros">
            <div className="filtros-content">
              <div className="filtros-row-horizontal">
                <div className="filtro-item">
                  <label>Fecha Inicio:</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="filtro-item">
                  <label>Fecha Fin:</label>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="filtro-item filtro-destino">
                  <label>Destino:</label>
                  <Select
                    value={destinoFiltro}
                    onChange={setDestinoFiltro}
                    options={opcionesDestinos}
                    placeholder="Todos..."
                    isClearable
                    className="select-filtro-destino"
                    classNamePrefix="select"
                  />
                </div>

                <div className="btn-mostrar-salidas">
                  <button className="btn-buscar" onClick={cargarSalidas}>
                    Mostrar Salidas
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="panel-listado">
            <div className="tabla-container">
              {loading ? (
                <div className="cargando">Cargando salidas...</div>
              ) : salidasAgrupadas.length === 0 ? (
                <div className="sin-datos">No se encontraron salidas en el periodo seleccionado.</div>
              ) : (
                <table className="tabla-salidas">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Destino</th>
                      <th>Motivo</th>
                      <th>Ingredientes</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salidasAgrupadas.map(salida => (
                      <tr key={salida.idSalida}>
                        <td>{salida.fecha}</td>
                        <td className="destino-salida">
                          <span className="tag-destino">{salida.nombreDestino}</span>
                        </td>
                        <td className="motivo-salida">{salida.motivo}</td>
                        <td className="cantidad-ingredientes">{salida.ingredientes.length}</td>
                        <td>
                          <div className="acciones-salida">
                            <button
                              onClick={() => verDetalleSalida(salida)}
                              className="btn-accion btn-ver"
                              title="Ver detalle"
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => confirmarEliminarSalida(salida)}
                              className="btn-accion btn-eliminar"
                              title="Eliminar salida"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="acciones-container">
              <div className="info-salidas">
                <span>Total de registros: {salidasAgrupadas.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Modal para crear salida */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Nueva Salida de Inventario"
        onSave={guardarSalida}
        saveLabel="Registrar"
        isSaving={guardando}
        size="xlarge"
        saveIcon={<FaTruck />}
        saveButtonType="button"
      >
        {/* Selector de destino */}
        <div className="form-group-salida destino-obligatorio">
          <label>Destino *</label>
          <Select
            value={destinoSeleccionado}
            onChange={setDestinoSeleccionado}
            options={opcionesDestinos}
            placeholder="Seleccione destino..."
            isSearchable
            isClearable
            noOptionsMessage={() => "No hay destinos disponibles"}
            className="select-destino"
            classNamePrefix="select"
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '42px',
                borderColor: state.isFocused ? '#2e7d32' : '#d1d5db',
                boxShadow: state.isFocused ? '0 0 0 3px rgba(46, 125, 50, 0.1)' : 'none',
                '&:hover': { borderColor: '#2e7d32' }
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#2e7d32' : state.isFocused ? '#f5f5f5' : 'white',
                color: state.isSelected ? 'white' : '#333'
              })
            }}
          />
        </div>

        {/* Selector de modo */}
        <div className="modo-selector">
          <button
            type="button"
            className={`modo-btn ${modoSeleccion === 'ingrediente' ? 'activo' : ''}`}
            onClick={() => {
              setModoSeleccion('ingrediente');
              setPlatilloSeleccionado(null);
              setPlatillosAgregados([]);
              setIngredientesConsolidados([]);
            }}
          >
            Por Ingrediente
          </button>
          <button
            type="button"
            className={`modo-btn ${modoSeleccion === 'platillo' ? 'activo' : ''}`}
            onClick={() => {
              setModoSeleccion('platillo');
              setIngredienteSeleccionado(null);
            }}
          >
            Por Platillo (Receta)
          </button>
        </div>

        {modoSeleccion === 'ingrediente' ? (
          <>
            <div className="form-group-salida">
              <label>Ingrediente *</label>
              <Select
                value={ingredienteSeleccionado}
                onChange={setIngredienteSeleccionado}
                options={opcionesIngredientes}
                placeholder="Buscar ingrediente..."
                isSearchable
                isClearable
                noOptionsMessage={() => "No se encontraron ingredientes"}
                className="select-producto"
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '42px',
                    borderColor: '#d1d5db',
                    '&:hover': { borderColor: '#2e7d32' }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#2e7d32' : state.isFocused ? '#f5f5f5' : 'white',
                    color: state.isSelected ? 'white' : '#333'
                  })
                }}
              />
            </div>

            <FormGrid columns={2} gap="16px">
              <FormField
                label="Cantidad"
                type="number"
                name="cantidad"
                value={formData.cantidad}
                onChange={(e) => {
                  const valor = limitarDecimales(e.target.value, 3);
                  setFormData(prev => ({ ...prev, cantidad: valor }));
                }}
                required
                placeholder="0.000"
                step="0.001"
                min="0"
              />

              <FormField
                label="Fecha"
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleInputChange}
                icon={<FaCalendarAlt />}
              />
            </FormGrid>
          </>
        ) : (
          <>
            <div className="platillos-section">
              <div className="agregar-platillo-row">
                <div className="select-platillo-container">
                  <Select
                    value={platilloSeleccionado}
                    onChange={setPlatilloSeleccionado}
                    options={opcionesPlatillos}
                    placeholder="Buscar platillo..."
                    isSearchable
                    isClearable
                    noOptionsMessage={() => "No se encontraron platillos"}
                    className="select-producto"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#2e7d32' }
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#2e7d32' : state.isFocused ? '#f5f5f5' : 'white',
                        color: state.isSelected ? 'white' : '#333'
                      })
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-agregar-platillo"
                  onClick={agregarPlatillo}
                >
                  <FaPlus /> Agregar
                </button>
              </div>

              {platillosAgregados.length > 0 && (
                <div className="lista-platillos-agregados">
                  <label>Platillos a enviar:</label>
                  <div className="tabla-platillos">
                    <div className="header-platillos">
                      <span className="col-platillo-nombre">Platillo</span>
                      <span className="col-platillo-cantidad">Cantidad</span>
                      <span className="col-platillo-acciones">Acciones</span>
                    </div>
                    {platillosAgregados.map(platillo => (
                      <div key={platillo.idProducto} className="row-platillo">
                        <span className="col-platillo-nombre">{platillo.nombre}</span>
                        <span className="col-platillo-cantidad">
                          <input
                            type="number"
                            value={platillo.cantidad}
                            onChange={(e) => cambiarCantidadPlatillo(platillo.idProducto, limitarDecimales(e.target.value, 3))}
                            min="0.001"
                            step="0.001"
                          />
                        </span>
                        <span className="col-platillo-acciones">
                          <button
                            type="button"
                            className="btn-eliminar-platillo"
                            onClick={() => eliminarPlatillo(platillo.idProducto)}
                          >
                            <FaTrash />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <FormField
              label="Fecha"
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={handleInputChange}
              icon={<FaCalendarAlt />}
            />

            {ingredientesConsolidados.length > 0 && (
              <div className="ingredientes-receta">
                <label>Ingredientes consolidados a enviar:</label>
                <div className="tabla-ingredientes-receta">
                  <div className="header-ingredientes">
                    <span className="col-check"></span>
                    <span className="col-nombre">Ingrediente</span>
                    <span className="col-origen">Origen</span>
                    <span className="col-salida">Cant. a Enviar</span>
                  </div>
                  {ingredientesConsolidados.map((ing) => (
                    <div key={ing.idIngrediente} className="row-ingrediente">
                      <span className="col-check">
                        <input
                          type="checkbox"
                          checked={ing.seleccionado}
                          onChange={(e) => handleIngredienteConsolidadoChange(ing.idIngrediente, 'seleccionado', e.target.checked)}
                        />
                      </span>
                      <span className="col-nombre">{ing.nombre}</span>
                      <span className="col-origen">
                        {ing.preparados && ing.preparados.length > 0
                          ? <span className="tag-preparado" title={ing.preparados.join(', ')}>Preparado</span>
                          : <span className="tag-directo">Directo</span>
                        }
                      </span>
                      <span className="col-salida">
                        <input
                          type="number"
                          value={limitarDecimales(ing.cantidadSalida, 3)}
                          onChange={(e) => handleIngredienteConsolidadoChange(ing.idIngrediente, 'cantidadSalida', limitarDecimales(e.target.value, 3))}
                          step="0.001"
                          min="0"
                          disabled={!ing.seleccionado}
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {platillosAgregados.length > 0 && ingredientesConsolidados.length === 0 && (
              <div className="sin-ingredientes">
                Los platillos seleccionados no tienen ingredientes en sus recetas.
              </div>
            )}
          </>
        )}

        <FormField
          label="Motivo / Comentario"
          type="textarea"
          name="motivo"
          value={formData.motivo}
          onChange={handleInputChange}
          placeholder="Describa el motivo de la salida (opcional)"
          icon={<FaClipboardList />}
        />
      </FormModal>

      {/* Modal de confirmacion para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarSalida}
        title="Eliminar Salida"
        message="¿Está seguro de eliminar esta salida? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />

      {/* Modal para ver detalle de salida */}
      <FormModal
        isOpen={showDetalleModal}
        onClose={handleCloseDetalleModal}
        title="Detalle de Salida"
        size="large"
        showCancel={false}
        saveLabel="Cerrar"
        onSave={handleCloseDetalleModal}
        saveButtonType="button"
      >
        {salidaDetalle && (
          <div className="detalle-salida-modal">
            <div className="detalle-info">
              <p><strong>Fecha:</strong> {salidaDetalle.fecha}</p>
              <p><strong>Destino:</strong> <span className="tag-destino-detalle">{salidaDetalle.nombreDestino}</span></p>
              <p><strong>Motivo:</strong> {salidaDetalle.motivo || '-'}</p>
            </div>
            <div className="detalle-ingredientes">
              <label>Ingredientes:</label>
              <table className="tabla-detalle-ingredientes">
                <thead>
                  <tr>
                    <th>Ingrediente</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {salidaDetalle.ingredientes.map(ing => (
                    <tr key={ing.idSalidaDetalle}>
                      <td>{ing.nombreIngrediente}</td>
                      <td className="cantidad-detalle">{parseFloat(ing.cantidad).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </FormModal>
    </div>
  );
};

export default SalidaInventario;
